"use client";
import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import TableGruposEstudiantes from './TableGruposEstudiantes';
import CardGruposEstudiantesModal from './CardGruposEstudiantesModal';
import { createGroup, getGroups, updateGroup, deleteGroup } from '../../api/groups';
import LoaderOverlay from '../../components/LoaderOverlay';
import Toast from '../DocentesScreen/Toast';
import styles from '../../styles/PantallaDeProgramasAcademicos.v2.module.css';
import estudiantesStyles from '../../styles/EstudiantesStyles/Estudiantes.module.css';
import { useSidebar } from '../../components/Sidebar';
import { usePermissions } from '../../hooks/usePermissions';

const PantallaGruposEstudiantes = () => {
  const { isCollapsed } = useSidebar();
  const { canCreate } = usePermissions();
  const puedeCrear = canCreate('Otros');

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('create'); // 'create' | 'view' | 'edit'
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [grupos, setGrupos] = useState([]);
  const [selected, setSelected] = useState(null);
  const [vista, setVista] = useState('tarjetas'); // 'tabla' | 'tarjetas'

  // Filtros para el reporte
  const [filtroGrupo, setFiltroGrupo] = useState('');
  const [gruposDisponibles, setGruposDisponibles] = useState([]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const data = await getGroups();
        if (mounted) setGrupos(data.results || []);
      } catch (e) {
        console.error('Error cargando grupos:', e);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  // Ejemplo: cargar grupos disponibles al montar
  useEffect(() => {
    // Aquí deberías cargar los grupos reales desde la API
    setGruposDisponibles([
      { id: '1', nombre: 'Grupo 1' },
      { id: '2', nombre: 'Grupo 2' },
      // ...otros grupos
    ]);
  }, []);

  const handleView = async (grupo) => {
    const id = grupo.id_grupo || grupo.id;
    if (!id) {
      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: 'ID de grupo no válido' } }));
      return;
    }
    try {
      setLoading(true);
      // Cambia getGroup por getGroups en los lugares donde se usa para obtener un grupo específico
      // Si necesitas obtener un grupo por ID, filtra el array devuelto por getGroups()
      // Ejemplo:
      // const grupo = (await getGroups()).find(g => g.id_grupo === id);
      const grupo = (await getGroups()).find(g => g.id_grupo === id);
      setSelected(grupo);
      setMode('view');
      setOpen(true);
    } catch (e) {
      console.error('Error cargando grupo:', e);
      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: 'No se pudo cargar el grupo' } }));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      console.log('Buscando grupos con término (server-side):', query);
      const data = await getGroups({ page: 1, pageSize: 20, search: query });
      setGrupos(data.results || []);
    } catch (e) {
      console.error('Error buscando grupos:', e);
      window.dispatchEvent(new CustomEvent('toast:show', {
        detail: { type: 'error', message: 'No se pudieron cargar los grupos filtrados' }
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (grupo) => {
    const id = grupo.id_grupo || grupo.id;
    if (!id) {
      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: 'ID de grupo no válido' } }));
      return;
    }
    try {
      setLoading(true);
      const grupoData = (await getGroups()).find(g => g.id_grupo === id);
      setSelected(grupoData);
      setMode('edit');
      setOpen(true);
    } catch (e) {
      console.error('Error cargando grupo:', e);
      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: 'No se pudo cargar el grupo' } }));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (grupo) => {
    const id = grupo.id_grupo || grupo.id;
    if (!id) {
      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: 'ID de grupo no válido' } }));
      return;
    }
    if (!window.confirm(`¿Eliminar el grupo "${grupo.nombre}"?`)) return;
    try {
      setLoading(true);
      await deleteGroup(id);
      const updated = await getGroups();
      setGrupos(updated.results || []);
      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'success', message: 'Grupo eliminado' } }));
    } catch (e) {
      console.error('Error eliminando grupo:', e);
      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: 'No se pudo eliminar el grupo' } }));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      setLoading(true);
      const grupoId = selected?.id_grupo || selected?.id;
      if (mode === 'edit' && grupoId) {
        await updateGroup(grupoId, formData);
        window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'success', message: 'Grupo actualizado' } }));
      } else {
        await createGroup(formData);
        window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'success', message: 'Grupo creado' } }));
      }
      const updated = await getGroups();
      setGrupos(updated.results || []);
      setOpen(false);
      setSelected(null);
      setMode('create');
    } catch (err) {
      console.error('Error guardando grupo:', err);
      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: `No se pudo guardar el grupo. ${err?.message || ''}` } }));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpen(false);
    setSelected(null);
    setMode('create');
  };

  // Elimina los datos de prueba, usar idEstudiante para consulta real
  // Ejemplo: obtener el id del estudiante logueado
  const idEstudianteActual = selected?.id_estudiante || null;

  return (
    <div className={styles.mainContainer}>
      <div className={`${styles.layout} ${isCollapsed ? styles.sidebarCollapsedLayout : ''}`}>
        <div className={styles.sidebarColumn}>
          <Sidebar />
        </div>
        <div className={styles.contentColumn}>
          <HeaderGlobal title="Grupos de Estudiantes" />
          <main className={styles.mainContent}>
            <div className={styles.contentArea}>
              <div className={styles.contentWrapper} style={{ position: 'relative' }}>
                <LoaderOverlay visible={loading} />
                <div className={estudiantesStyles.searchSection}>
                  <div className={estudiantesStyles.searchCard}>
                    <div className={styles.programsSearchRow} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '12px' }}>
                      {/* Filtro de grupo eliminado */}
                      <div style={{ flex: 2, minWidth: 220, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                        <div className={estudiantesStyles.searchInputGroup}>
                          <svg
                            className={estudiantesStyles.searchIcon}
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                              stroke="#898d8d"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <input
                            type="text"
                            className={estudiantesStyles.searchInput}
                            placeholder="Buscar estudiante, grupo o asesor..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') handleSearch();
                            }}
                            style={{ width: '100%' }}
                          />
                        </div>
                      </div>
                      <div style={{ flex: 'none', alignSelf: 'flex-end' }}>
                        <button
                          className={styles.buscarButtonProgramas}
                          onClick={handleSearch}
                          style={{ minWidth: 110 }}
                        >
                          Buscar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Selector de vista eliminado. Solo se muestra la vista de tarjetas por defecto. */}
                <CardGruposEstudiantesModal idEstudiante={idEstudianteActual} query={query} />
              </div>
            </div>
          </main>
        </div>
      </div>
      <Toast />
    </div>
  );
};

export default PantallaGruposEstudiantes;
